/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useRef, useState } from "react"
import { ChevronRightUniversal, MoreInfoOutlined } from "@duxton-web/icons"
import { Button, Grid, Modal, Typography } from "@duxton-web/ui"
import classNames from "classnames"

import { useTranslationsMap } from "common/utils/i18n"
import { BottomSheet } from "components/common"
import notification from "components/common/notification"
import { PRODUCT_CATALOGUE_HELP_CENTRE_URL } from "ecommerce/constants/constants"
import { useBackDismissibleDrawer } from "ecommerce/hooks/useBackDismissibleDrawer"
import { useHideAppBottomNavWhileBottomSheetOpen } from "hooks/useHideAppBottomNavWhileBottomSheetOpen"

import {
  PmfFeedbackContent,
  PmfFeedbackPayload,
  PmfFeedbackState,
} from "./PmfFeedbackContent"
import { PmfFeedbackEvent, PmfFeedbackRating } from "./pmfFeedbackOptions"
import styles from "./PmfFeedbackWrapper.modules.css"

const pmfFeedbackNotificationImages: Record<PmfFeedbackRating, string> = {
  GOOD: require("assets/images/ecommerce/good_feedback_submitted.png"),
  BAD: require("assets/images/ecommerce/bad_feedback_submitted.png"),
}

interface PmfFeedbackWrapperProps {
  open: boolean
  /** Triggering event — selects the option list. Undefined falls back to GENERAL. */
  event?: PmfFeedbackEvent
  onClose: () => void
  onStateChange?: (state: PmfFeedbackState) => void
  onSubmit?: (payload: PmfFeedbackPayload) => void
}

export const PmfFeedbackModal: FC<PmfFeedbackWrapperProps> = ({
  open,
  event,
  onClose,
  onStateChange,
  onSubmit,
}) => {
  const textMap = useTranslationsMap({
    goodFeedbackSubmitted:
      "ecommerce.sellerCentre.pmfFeedback.notification.goodFeedbackSubmitted",
    continueOurBest:
      "ecommerce.sellerCentre.pmfFeedback.notification.continueOurBest",
    badFeedbackSubmitted:
      "ecommerce.sellerCentre.pmfFeedback.notification.badFeedbackSubmitted",
    workOnFeedback:
      "ecommerce.sellerCentre.pmfFeedback.notification.workOnFeedback",
  })

  const handleSubmitFeedback = (payload: PmfFeedbackPayload) => {
    onSubmit?.(payload)
    notification.success({
      message:
        payload.rating === "GOOD"
          ? textMap.goodFeedbackSubmitted
          : textMap.badFeedbackSubmitted,
      description:
        payload.rating === "GOOD"
          ? textMap.continueOurBest
          : textMap.workOnFeedback,
    })
  }

  return (
    <Modal
      destroyOnClose
      open={open}
      onCancel={onClose}
      footer={null}
      width={375}
      maskClosable={false}
      wrapClassName={styles.wrap}
      className={styles.modal}
    >
      <PmfFeedbackContent
        event={event}
        reserveCloseButtonSpace
        onStateChange={onStateChange}
        onSubmit={handleSubmitFeedback}
      />
    </Modal>
  )
}

export const PmfFeedbackSheet: FC<PmfFeedbackWrapperProps> = ({
  open,
  event,
  onClose,
  onStateChange,
  onSubmit,
}) => {
  const [notificationVal, setNotificationVal] = useState<PmfFeedbackRating>()
  const [showHelpCentreLink, setShowHelpCentreLink] = useState(true)
  const {
    open: isFeedbackDrawerOpen,
    setOpen: setFeedbackDrawerOpen,
  } = useBackDismissibleDrawer({ name: "pmfFeedback" })
  const {
    open: isNotificationDrawerOpen,
    setOpen: setNotificationDrawerOpen,
  } = useBackDismissibleDrawer({ name: "pmfFeedbackNotification" })
  const wasFeedbackDrawerOpenRef = useRef(false)
  const closeFeedbackRequestedRef = useRef(false)
  const wasNotificationDrawerOpenRef = useRef(false)

  useHideAppBottomNavWhileBottomSheetOpen(
    open || !!notificationVal,
    "PmfFeedbackSheet",
  )

  useEffect(() => {
    if (open) {
      setShowHelpCentreLink(true)
      setNotificationVal(undefined)
      setFeedbackDrawerOpen(true)
      return
    }

    setFeedbackDrawerOpen(false)
  }, [open, setFeedbackDrawerOpen])

  useEffect(() => {
    if (wasFeedbackDrawerOpenRef.current && !isFeedbackDrawerOpen) {
      if (open && !closeFeedbackRequestedRef.current) onClose()
      closeFeedbackRequestedRef.current = false
    }

    wasFeedbackDrawerOpenRef.current = isFeedbackDrawerOpen
  }, [isFeedbackDrawerOpen, onClose, open])

  useEffect(() => {
    if (wasNotificationDrawerOpenRef.current && !isNotificationDrawerOpen) {
      setNotificationVal(undefined)
    }

    wasNotificationDrawerOpenRef.current = isNotificationDrawerOpen
  }, [isNotificationDrawerOpen])

  const textMap = useTranslationsMap({
    visitHelpCentre: "ecommerce.sellerCentre.pmfFeedback.visitHelpCentre",
    goodFeedbackSubmitted:
      "ecommerce.sellerCentre.pmfFeedback.notification.goodFeedbackSubmitted",
    continueOurBest:
      "ecommerce.sellerCentre.pmfFeedback.notification.continueOurBest",
    badFeedbackSubmitted:
      "ecommerce.sellerCentre.pmfFeedback.notification.badFeedbackSubmitted",
    workOnFeedback:
      "ecommerce.sellerCentre.pmfFeedback.notification.workOnFeedback",
    close: "ecommerce.sellerCentre.pmfFeedback.close",
  })

  const handleSubmitFeedback = (payload: PmfFeedbackPayload) => {
    onSubmit?.(payload)
    // The notification takes over the feedback drawer's history entry, which
    // also closes the feedback drawer. Flag it as an intentional close so the
    // drawer-close effect below does not read the takeover as a dismissal and
    // call onClose — a consumer that keeps `open` true after submit would
    // otherwise get a spurious dismissal (and its analytics).
    closeFeedbackRequestedRef.current = true
    setNotificationVal(payload.rating)
    setNotificationDrawerOpen(true)
  }

  const handleCloseFeedback = () => {
    closeFeedbackRequestedRef.current = true
    setFeedbackDrawerOpen(false)
    onClose()
  }

  const handleCloseNotification = () => {
    setNotificationDrawerOpen(false)
    setNotificationVal(undefined)
  }

  return (
    <>
      <BottomSheet
        open={isFeedbackDrawerOpen}
        onClose={handleCloseFeedback}
        height="auto"
        destroyOnClose
      >
        {showHelpCentreLink && (
          <Button
            type="text"
            block
            size="large"
            className={styles.helpCentreLink}
            onClick={() => {
              window.open(
                PRODUCT_CATALOGUE_HELP_CENTRE_URL,
                "_blank",
                "noopener",
              )
            }}
          >
            <span className={styles.helpCentreLinkRow}>
              <MoreInfoOutlined aria-hidden />
              <span>{textMap.visitHelpCentre}</span>
              <ChevronRightUniversal
                aria-hidden
                className={styles.helpCentreChevron}
              />
            </span>
          </Button>
        )}
        <PmfFeedbackContent
          event={event}
          onRatingChange={() => setShowHelpCentreLink(false)}
          onStateChange={onStateChange}
          onSubmit={handleSubmitFeedback}
        />
      </BottomSheet>
      <BottomSheet
        open={isNotificationDrawerOpen}
        onClose={handleCloseNotification}
        height="auto"
        destroyOnClose
      >
        {!!notificationVal && (
          <>
            <img
              src={pmfFeedbackNotificationImages[notificationVal]}
              alt={notificationVal}
              className={classNames(styles.notificationImage, {
                [styles.notificationImageGood]: notificationVal === "GOOD",
                [styles.notificationImageBad]: notificationVal === "BAD",
              })}
            />
            <div className={styles.notificationText}>
              <Typography.Body level={1} strong className={styles.title}>
                {notificationVal === "GOOD"
                  ? textMap.goodFeedbackSubmitted
                  : textMap.badFeedbackSubmitted}
              </Typography.Body>
              <Typography.Body level={2} className={styles.subtitle}>
                {notificationVal === "GOOD"
                  ? textMap.continueOurBest
                  : textMap.workOnFeedback}
              </Typography.Body>
            </div>
            <Button
              type="primary"
              block
              size="large"
              onClick={handleCloseNotification}
            >
              {textMap.close}
            </Button>
          </>
        )}
      </BottomSheet>
    </>
  )
}

export const PmfFeedbackWrapper: FC<PmfFeedbackWrapperProps> = ({
  open,
  event,
  onClose,
  onStateChange,
  onSubmit,
}) => {
  const { md } = Grid.useBreakpoint()

  if (md === undefined) return null

  return md ? (
    <PmfFeedbackModal
      open={open}
      event={event}
      onClose={onClose}
      onStateChange={onStateChange}
      onSubmit={onSubmit}
    />
  ) : (
    <PmfFeedbackSheet
      open={open}
      event={event}
      onClose={onClose}
      onStateChange={onStateChange}
      onSubmit={onSubmit}
    />
  )
}
